/**
 * Запуск страницы: что известно до загрузки скрипта Telegram.
 *
 * `BOOT_SCRIPT` — inline-скрипт в `<head>` (вставляет vite.config.ts), выполняется до любого другого кода и будущего
 * трекера аналитики (I3):
 * - убирает `tgWebAppData` из хэша адреса (в нём имя, @username, Telegram id и подпись входа — в аналитику не должны
 *   попасть), кладёт initData в sessionStorage и `window.__nfgInitData`; остальные `tgWebApp*` (версия, платформа, тема)
 *   оставляет — без них telegram-web-app.js считает клиент версией 6.0;
 * - чинит `&amp;` в query (так Telegram портит адрес Mini App с `&` из BotFather);
 * - ставит `data-miniapp` на `<html>` и кешированные `--app-inset-*` до первой отрисовки (иначе сдвиг макета);
 * - `lang` на `/ru` и `/uk`.
 *
 * Функции ниже читают то же самое уже из приложения. Ничего не трогают при импорте — модуль импортирует и vite.config.
 */
import type { Platform } from "@newfungame/contracts";

export const INIT_DATA_KEY = "nfg:tgInitData";
export const INSETS_KEY = "nfg:tgInsets";
/** Метка `startapp`: латиница, цифры, `_` и `-`, до 64 символов (ограничение Telegram). */
export const START_PARAM_RE = /^[A-Za-z0-9_-]{1,64}$/;

declare global {
  interface Window {
    /** initData, снятая из хэша `BOOT_SCRIPT`-ом (если sessionStorage недоступен). */
    __nfgInitData?: string;
  }
}

export const BOOT_SCRIPT =
  `(function(){var K=${JSON.stringify(INIT_DATA_KEY)},I=${JSON.stringify(INSETS_KEY)},de=document.documentElement,d=null;` +
  `try{var h=location.hash,s=location.search,c=false;` +
  `if(h.indexOf("tgWebAppData=")>=0){var hp=new URLSearchParams(h.slice(1));d=hp.get("tgWebAppData");` +
  `if(d){window.__nfgInitData=d;try{sessionStorage.setItem(K,d)}catch(e){}}` +
  `hp.delete("tgWebAppData");h=hp.toString();h=h?"#"+h:"";c=true}` +
  `if(s.indexOf("&amp;")>=0){s=s.split("&amp;").join("&");c=true}` +
  `if(c)history.replaceState(history.state,"",location.pathname+s+h);` +
  `if(!d){try{d=sessionStorage.getItem(K)}catch(e){}}` +
  `if(d||/[?&]tgWebAppStartParam=/.test(s))de.setAttribute("data-miniapp","1")}catch(e){}` +
  `try{var ai=JSON.parse(sessionStorage.getItem(I)||"null");if(ai)["top","bottom","left","right"].forEach(function(k){` +
  `var v=Math.round(+ai[k]);if(v>0)de.style.setProperty("--app-inset-"+k,v+"px")})}catch(e){}` +
  `try{var lm=/^\\/(ru|uk)(\\/|$)/i.exec(location.pathname);if(lm)de.lang=lm[1].toLowerCase()}catch(e){}})();`;

/** Хранилище сессии или `null` (приватный режим, запрет cookie). */
export function sessionStore(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Что известно о запуске: Mini App или браузер, initData, метка и платформа. */
export interface LaunchInfo {
  miniApp: boolean;
  /** initData как есть (для `authTelegram`); "" — браузер. */
  initData: string;
  /** `start_param` (`?startapp=`), проверенная по {@link START_PARAM_RE}. */
  startParam: string | null;
  /** `language_code` пользователя Telegram из initData. */
  telegramLanguage: string | null;
  /** `tgWebAppPlatform` из хэша (ios, android, tdesktop, weba…). */
  telegramPlatform: string | null;
}

export interface LaunchSource {
  search: string;
  hash: string;
  /** initData, снятая `BOOT_SCRIPT`-ом. */
  bootInitData?: string | null;
  storage?: Pick<Storage, "getItem"> | null;
}

function readStored(storage: LaunchSource["storage"]): string {
  try {
    return storage?.getItem(INIT_DATA_KEY) ?? "";
  } catch {
    return "";
  }
}

/** Проверенная метка или `null`. */
export function validStartParam(v: unknown): string | null {
  return typeof v === "string" && START_PARAM_RE.test(v) ? v : null;
}

/** Разобрать запуск. Чистая функция — тестируется без браузера. */
export function readLaunch(src: LaunchSource): LaunchInfo {
  const hash = new URLSearchParams(src.hash.replace(/^#/, ""));
  const query = new URLSearchParams(src.search);
  const initData = hash.get("tgWebAppData") || src.bootInitData || readStored(src.storage);
  const fields = new URLSearchParams(initData);
  let telegramLanguage: string | null = null;
  try {
    const user = JSON.parse(fields.get("user") ?? "null") as { language_code?: unknown } | null;
    if (typeof user?.language_code === "string") telegramLanguage = user.language_code;
  } catch {
    // битый user — язык выберут по-другому
  }
  return {
    miniApp: initData !== "" || query.has("tgWebAppStartParam"),
    initData,
    startParam: validStartParam(query.get("tgWebAppStartParam")) ?? validStartParam(fields.get("start_param")),
    telegramLanguage,
    telegramPlatform: hash.get("tgWebAppPlatform"),
  };
}

/** Платформа для аналитики (`app_open`) по `Telegram.WebApp.platform`. */
export function analyticsPlatform(miniApp: boolean, tgPlatform: string | null | undefined): Platform {
  if (!miniApp) return "browser";
  const p = (tgPlatform ?? "").toLowerCase();
  if (p === "ios") return "telegram_ios";
  if (p.startsWith("android")) return "telegram_android";
  if (p === "tdesktop" || p === "macos" || p === "unigram") return "telegram_desktop";
  return "telegram_web";
}

/** Записать `--app-inset-*` на элемент (обычно `<html>`). */
export function applyAppInsets(
  root: { style: { setProperty: (name: string, value: string) => void } },
  insets: { top: number; bottom: number; left: number; right: number },
): void {
  for (const side of ["top", "bottom", "left", "right"] as const) {
    root.style.setProperty(`--app-inset-${side}`, `${Math.max(0, Math.round(insets[side] || 0))}px`);
  }
}
